import { useAuth } from "@clerk/clerk-react";
import { useActiveAgent } from "../../contexts/ActiveAgentContext";
import { IAgent } from "../../types/agent";

export function useApi(agentRequired = true, shouldBuildUrl = true) {
  const { getToken } = useAuth();

  let activeAgent: IAgent | null = null;
  if (agentRequired) {
    activeAgent = useActiveAgent().activeAgent;
  }

  const buildUrl = (endpoint: string): string => {
    if (agentRequired && activeAgent === null) {
      throw new Error("No active agent selected");
    }

    const processedEndpoint = endpoint.startsWith("/")
      ? endpoint.slice(1)
      : endpoint;
    if (agentRequired) {
      return `${process.env.EXPO_PUBLIC_API_BASE}/agents/${activeAgent?.id}/${processedEndpoint}`;
    }
    return `${process.env.EXPO_PUBLIC_API_BASE}/${processedEndpoint}`;
  };

  const handleRequest = async <T>(
    url: string,
    method: string,
    body?: object | FormData
  ): Promise<T> => {
    const token = await getToken();
    if (!token) {
      throw new Error("No authentication token provided");
    }

    try {
      const isFormData = body instanceof FormData;
      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(body && !isFormData && { "Content-Type": "application/json" }),
        },
        ...(body && {
          body: isFormData ? body : JSON.stringify(body),
        }),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      // Only try to parse JSON if we expect a response body
      // Check if response has content before trying to parse JSON
      const contentLength = response.headers.get("content-length");
      const contentType = response.headers.get("content-type");
      if (contentLength !== "0" && contentType?.includes("application/json")) {
        return await response.json();
      }

      return {} as T;
    } catch (error) {
      throw new Error(`Request failed: ${error}`);
    }
  };

  return {
    get: async <T>(endpoint: string): Promise<T> => {
      return handleRequest<T>(
        shouldBuildUrl ? buildUrl(endpoint) : endpoint,
        "GET"
      );
    },

    post: async <T>(endpoint: string, body?: object | FormData): Promise<T> => {
      return handleRequest<T>(
        shouldBuildUrl ? buildUrl(endpoint) : endpoint,
        "POST",
        body
      );
    },

    patch: async <T>(
      endpoint: string,
      body?: object | FormData
    ): Promise<T> => {
      return handleRequest<T>(
        shouldBuildUrl ? buildUrl(endpoint) : endpoint,
        "PATCH",
        body
      );
    },

    put: async <T>(endpoint: string, body?: object | FormData): Promise<T> => {
      return handleRequest<T>(
        shouldBuildUrl ? buildUrl(endpoint) : endpoint,
        "PUT",
        body
      );
    },

    delete: async <T>(endpoint: string): Promise<T> => {
      return handleRequest<T>(
        shouldBuildUrl ? buildUrl(endpoint) : endpoint,
        "DELETE"
      );
    },
  };
}
